import { useState } from 'react';
import { API_URL, apiFetch } from '../utils/api';

type Counts = { total: number; registered: number; counselled_only: number; matched: number; unmatched: number; ambiguous: number; no_phone: number; invalid: number; duplicates: number };
type Committed = Counts & { saved: number; registrations_recorded: number; already_recorded: number; conflicts: string[] };

// Column names as the counsellors' register writes them (any month sheet). Names are never sent.
const aliases: Record<string, string[]> = {
  patient_id: ['patient id', 'patient no', 'patient number', 'uhid'], phone: ['mobile', 'phone', 'patient mobile', 'mobile no', 'contact'],
  counselled_on: ['date', 'counselling date'], registered_on: ['registration date', 'reg date'], counselled_for: ['counselled for', 'counseled for'],
  referral: ['referal', 'referral', 'source'], counsellor: ['counsilor', 'counsellor', 'counselor'],
};
const norm = (value: unknown) => String(value ?? '').trim().toLowerCase().replace(/[_.-]+/g, ' ').replace(/\s+/g, ' ');
const CENTRES = ['Berhampur', 'Bhubaneswar', 'Angul'];

export default function IvfRegisterPanel() {
  const [center, setCenter] = useState('');
  const [payload, setPayload] = useState<{ center: string; rows: Array<Record<string, unknown>> } | null>(null);
  const [preview, setPreview] = useState<Counts | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  async function choose(file: File) {
    if (!center) { setMessage('Pick the centre first.'); return; }
    setBusy(true); setMessage('Reading every month sheet…'); setPreview(null);
    try {
      const XLSX = await import('xlsx');
      const book = XLSX.read(await file.arrayBuffer(), { type: 'array', cellDates: false });
      const rows = book.SheetNames.flatMap((sheet) => XLSX.utils.sheet_to_json<Record<string, unknown>>(book.Sheets[sheet], { defval: '' }).map((row, index) => {
        const entries = Object.entries(row);
        const output: Record<string, unknown> = { sheet, source_row_number: index + 2 };
        for (const [target, names] of Object.entries(aliases)) output[target] = entries.find(([header]) => names.includes(norm(header)))?.[1] ?? '';
        return output;
      })).filter((row) => String(row.patient_id).trim());
      const next = { center, rows };
      const response = await apiFetch(`${API_URL}/api/admin/ivf-register/preview`, { method: 'POST', body: JSON.stringify(next) });
      if (!response.ok) throw new Error(await response.text());
      setPayload(next); setPreview(await response.json()); setMessage('Preview ready. Nothing saved yet.');
    } catch (e) { setMessage(String(e)); } finally { setBusy(false); }
  }

  async function commit() {
    if (!payload) return;
    setBusy(true);
    try {
      const response = await apiFetch(`${API_URL}/api/admin/ivf-register/commit`, { method: 'POST', body: JSON.stringify(payload) });
      if (!response.ok) throw new Error(await response.text());
      const r = await response.json() as Committed;
      setMessage(`Saved ${r.saved} rows. New registrations on CRM leads: ${r.registrations_recorded} (already recorded: ${r.already_recorded}).${r.conflicts.length ? ` Check: ${r.conflicts.join('; ')}` : ''}`);
      setPayload(null);
    } catch (e) { setMessage(String(e)); } finally { setBusy(false); }
  }

  return <div className="space-y-3">
    <div><h3 className="font-semibold">Daily IVF counselling register</h3><p className="text-xs text-slate-500">Upload the whole workbook (all months) every day. Re-uploading is safe: only new or changed rows count.</p></div>
    <select value={center} onChange={(e) => setCenter(e.target.value)} disabled={busy} className="block w-full rounded border p-2 text-sm">
      <option value="">Centre…</option>
      {CENTRES.map((name) => <option key={name}>{name}</option>)}
    </select>
    <input type="file" accept=".xlsx,.xls,.csv" disabled={busy || !center} onChange={(e) => e.target.files?.[0] && void choose(e.target.files[0])} className="block w-full text-sm" />
    {message && <p className="rounded bg-slate-50 p-2 text-xs">{message}</p>}
    {preview && payload && <>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">{(['registered', 'counselled_only', 'matched', 'unmatched', 'no_phone', 'invalid'] as const).map((key) => <div key={key} className="rounded border p-2"><b className="block text-lg">{preview[key]}</b>{key.replace('_', ' ')}</div>)}</div>
      <p className="text-xs text-slate-500">"no phone" = no mobile in the register and the patient ID is not yet in a Practo upload; it matches automatically once it is.</p>
      <button disabled={busy} onClick={() => void commit()} className="w-full rounded-lg bg-wechat-accent py-2 text-sm font-semibold text-white disabled:opacity-40">Save register</button>
    </>}
  </div>;
}
